import { useEffect, useRef, useState } from "react";

interface GlitchCollapseOverlayProps {
  active: boolean;
  fromAbyss?: boolean;
}

const GLITCH_CHARS = "アイウエオカキクケコサシスセソ†‡§¶#@!%&*?░▒▓█▄▀■□▪▫◆◇○●";

function randomGlitchChar() {
  return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
}

function scrambleText(text: string, intensity: number): string {
  return text
    .split("")
    .map((c) => (Math.random() < intensity ? randomGlitchChar() : c))
    .join("");
}

const COLLAPSE_PHRASES = [
  "TIME EXPIRED",
  "契約の機会は、失われた",
  "境界が閉じる",
  "再び試みよ…",
];

export default function GlitchCollapseOverlay({
  active,
  fromAbyss = false,
}: GlitchCollapseOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const [phase, setPhase] = useState<"glitch" | "collapse" | "void" | "reset">("glitch");
  const [displayPhrases, setDisplayPhrases] = useState(COLLAPSE_PHRASES);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // フェーズ遷移
  useEffect(() => {
    if (!active) return;
    setPhase("glitch");

    const t1 = setTimeout(() => setPhase("collapse"), 1000);
    const t2 = setTimeout(() => setPhase("void"), 2200);
    const t3 = setTimeout(() => setPhase("reset"), 3200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [active]);

  // テキストグリッチ
  useEffect(() => {
    if (!active) return;

    const glitchInterval = setInterval(() => {
      const p = phaseRef.current;
      const intensity =
        p === "glitch" ? 0.6 : p === "collapse" ? 0.9 : 0.3;
      setDisplayPhrases(
        COLLAPSE_PHRASES.map((phrase) => scrambleText(phrase, intensity))
      );
    }, 80);

    return () => clearInterval(glitchInterval);
  }, [active]);

  // Canvas グリッチノイズ
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let t = 0;

    function drawGlitch() {
      t++;
      const p = phaseRef.current;

      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      if (p === "void") {
        ctx.fillStyle = "rgba(0,0,0,0.95)";
        ctx.fillRect(0, 0, w, h);
        animRef.current = requestAnimationFrame(drawGlitch);
        return;
      }

      // ─ ノイズ背景 ─
      const noiseCount = p === "glitch" ? 400 : p === "collapse" ? 800 : 200;
      for (let i = 0; i < noiseCount; i++) {
        const x = Math.random() * w;
        const y = Math.random() * h;
        const size = Math.random() * 3;
        const alpha = Math.random() * (p === "collapse" ? 0.6 : 0.3);
        ctx.fillStyle = fromAbyss
          ? `rgba(${150 + Math.random() * 50}, ${Math.random() * 20}, ${Math.random() * 20}, ${alpha})`
          : `rgba(${150 + Math.random() * 100}, ${Math.random() * 30}, ${Math.random() * 30}, ${alpha})`;
        ctx.fillRect(x, y, size, size);
      }

      // ─ 水平スキャンライン ─
      const lineCount = p === "collapse" ? 12 : 6;
      for (let i = 0; i < lineCount; i++) {
        const y = Math.random() * h;
        const lineH = 1 + Math.random() * (p === "collapse" ? 8 : 3);
        const alpha = 0.3 + Math.random() * 0.5;
        ctx.fillStyle = fromAbyss
          ? `rgba(200, 0, 0, ${alpha})`
          : `rgba(255, 30, 30, ${alpha})`;
        ctx.fillRect(0, y, w, lineH);
      }

      // ─ 垂直ブロックずれ ─
      if (p === "collapse" && Math.random() < 0.4) {
        const blockCount = 3 + Math.floor(Math.random() * 8);
        for (let i = 0; i < blockCount; i++) {
          const by = Math.random() * h;
          const bh = 5 + Math.random() * 40;
          const offset = (Math.random() - 0.5) * 60;
          const blockAlpha = 0.15 + Math.random() * 0.3;
          ctx.fillStyle = `rgba(255, 0, 0, ${blockAlpha})`;
          ctx.fillRect(offset, by, w, bh);
        }
      }

      // ─ RGBシフト（色収差） ─
      if (p === "glitch" || p === "collapse") {
        const shiftX = (Math.random() - 0.5) * (p === "collapse" ? 20 : 8);
        const shiftY = (Math.random() - 0.5) * (p === "collapse" ? 8 : 3);

        // 赤チャンネルずれ
        ctx.fillStyle = `rgba(255, 0, 0, 0.06)`;
        ctx.fillRect(shiftX, shiftY, w, h);

        // 青チャンネルずれ
        ctx.fillStyle = `rgba(0, 0, 200, 0.06)`;
        ctx.fillRect(-shiftX, -shiftY, w, h);
      }

      // ─ 暗転オーバーレイ ─
      const overlayAlpha =
        p === "glitch" ? 0.5 + Math.sin(t * 0.3) * 0.2
          : p === "collapse" ? 0.7 + Math.sin(t * 0.5) * 0.15
          : 0.9;
      ctx.fillStyle = `rgba(0, 0, 0, ${overlayAlpha})`;
      ctx.fillRect(0, 0, w, h);

      // ─ 赤の稲妻グロー ─
      if (Math.random() < (p === "collapse" ? 0.3 : 0.1)) {
        const glowGrd = ctx.createRadialGradient(
          w / 2, h / 2, 0, w / 2, h / 2, w * 0.8
        );
        glowGrd.addColorStop(0, "rgba(200, 0, 0, 0.25)");
        glowGrd.addColorStop(0.5, "rgba(150, 0, 0, 0.1)");
        glowGrd.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = glowGrd;
        ctx.fillRect(0, 0, w, h);
      }

      animRef.current = requestAnimationFrame(drawGlitch);
    }

    animRef.current = requestAnimationFrame(drawGlitch);
    return () => cancelAnimationFrame(animRef.current);
  }, [active, fromAbyss]);

  if (!active) return null;

  const opacity =
    phase === "glitch" ? 1
      : phase === "collapse" ? 1
      : phase === "void" ? 1
      : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{
        opacity,
        transition: phase === "reset" ? "opacity 0.8s ease" : "none",
        pointerEvents: phase === "reset" ? "none" : "auto",
      }}
    >
      {/* Canvas ノイズ */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: "100%", height: "100%" }}
      />

      {/* テキスト */}
      {phase !== "void" && (
        <div
          className="relative z-10 flex flex-col items-center gap-4 text-center px-8"
          style={{
            transform:
              phase === "collapse"
                ? `translate(${(Math.random() - 0.5) * 8}px, ${(Math.random() - 0.5) * 4}px)`
                : "none",
          }}
        >
          {/* 巨大タイマー 00 */}
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(5rem, 15vw, 10rem)",
              fontWeight: 300,
              color:
                phase === "glitch"
                  ? "rgba(255, 60, 60, 0.9)"
                  : "rgba(200, 0, 0, 0.95)",
              textShadow:
                "0 0 30px rgba(255,0,0,0.7), 0 0 60px rgba(200,0,0,0.4), 4px 0 0 rgba(0,255,255,0.15), -4px 0 0 rgba(255,0,100,0.15)",
              letterSpacing: "0.1em",
              lineHeight: 1,
              animation: phase === "collapse" ? "glitchTextShake 0.08s ease-in-out infinite" : "none",
              filter: phase === "collapse" ? "blur(0.5px)" : "none",
            }}
          >
            00
          </div>

          {/* フレーズ */}
          <div className="flex flex-col items-center gap-2">
            {displayPhrases.map((phrase, i) => (
              <div
                key={i}
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: i === 0 ? "0.7rem" : "0.6rem",
                  letterSpacing: i === 0 ? "0.6em" : "0.3em",
                  textTransform: i === 0 ? "uppercase" : "none",
                  fontStyle: i > 0 ? "italic" : "normal",
                  color:
                    i === 0
                      ? "rgba(200, 30, 30, 0.8)"
                      : `rgba(255,255,255,${0.15 - i * 0.03})`,
                  textShadow:
                    i === 0
                      ? "2px 0 0 rgba(0,255,255,0.2), -2px 0 0 rgba(255,0,100,0.2)"
                      : "none",
                  transform:
                    phase === "collapse" && i % 2 === 0
                      ? "translateX(2px)"
                      : phase === "collapse"
                      ? "translateX(-2px)"
                      : "none",
                  transition: "transform 0.05s ease",
                }}
              >
                {phrase}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* void フェーズ：虚無 */}
      {phase === "void" && (
        <div
          className="relative z-10 text-center"
          style={{
            animation: "voidPulse 0.8s ease-in-out infinite alternate",
          }}
        >
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "0.6rem",
              letterSpacing: "0.8em",
              color: "rgba(100, 0, 0, 0.5)",
              textTransform: "uppercase",
            }}
          >
            …
          </div>
        </div>
      )}

      <style>{`
        @keyframes glitchTextShake {
          0%, 100% { transform: translateX(0) skewX(0); }
          25% { transform: translateX(-3px) skewX(-1deg); }
          75% { transform: translateX(3px) skewX(1deg); }
        }
        @keyframes voidPulse {
          from { opacity: 0.3; }
          to { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
